import react, { useState } from "react"

/*
Cookie utility mostly copied from https://github.com/devhammed/use-cookie and changed a bit to better fit our needs.

It's a custom hook, that accepts a default value and returns a get, set and delete method. 
Example usage:
    const [ cookie, setCookie, deleteCookie ] = useCookie('nameOfCookie');

*/
export type CookieOptions = Partial<{
  Expires: number
  Domain: string
  Path: string
  Secure: boolean
  HttpOnly: boolean
  MaxAge: number
  SameSite: string
}>

export const useCookie = (
  key: string,
  defaultValue?: any
): [string, typeof setCookie, typeof clearCookie] => {
  const [value, setValue] = useState(() => {
    // Gatsby SSR can't go further than here, since window will be undefined
    // This check avoids errors in the build process
    if (typeof window === "undefined" || !window.document) {
      return
    }

    let match = document.cookie?.match("(^|;) ?" + key + "=([^;]*)(;|$)")
    return match ? match[2] : defaultValue
  })

  const setCookie = (value: any, options?: CookieOptions): void => {
    // Default cookie options
    const cookieOptions = {
      Expires: 0,
      Secure: false,
      HttpOnly: false,
      MaxAge: 0,
      ...options,
    }

    // if our cookie value is an object or array, we want to save it as is to state for ease of use
    // Then we'll stringify, so we can save in browser
    setValue(value)
    const browserSafeValue =
      typeof value === "object" ? JSON.stringify(value) : value

    document.cookie = buildCookie(browserSafeValue, key, cookieOptions)
  }

  const clearCookie = (): void => {
    setCookie("", { Expires: -3600 })
    setValue(defaultValue)
  }

  return [value, setCookie, clearCookie]
}

const buildCookie = (value: any, key: any, cookieOptions: any): string => {
  let cookie: string = `${key}=${value}`
  const cookieKeys = Object.keys(cookieOptions)

  cookieKeys.forEach(key => {
    if (!cookieOptions[key]) {
      return
    }

    if (key === "Expires") {
      const date: Date = new Date()
      date.setTime(date.getTime() + 1000 * cookieOptions.Expires)
      cookie += `; Expires=${date.toUTCString()}`
    }

    if (typeof cookieOptions[key] !== "boolean") {
      cookie += `; ${key}=${cookieOptions[key]}`
      return
    }

    cookie += `; ${key}`
    return
  })

  return cookie
}
